import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid2, Typography, CircularProgress, Stack, Chip, alpha, Divider, Tooltip } from '@mui/material';
import api from '../../../utils/axios';
import { IChatbotQuestion } from 'sierra';
import { useApp } from '../../../contexts/AppContext';
import { TChatPanel } from './ChatFab';
import { SmartToy } from '@mui/icons-material';
import { useAuth } from '../../../hooks/useAuth';

const urlRegex = /https:\/\/[^\s]+/g;

const ChatBotMessage = memo(({ question }: { question: IChatbotQuestion }) => {
    const { lang } = useApp()

    const renderMessageWithLinks = useCallback((message: string) => {
        const parts = message.split(' ').map((part, index) => {
            if (urlRegex.test(part)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline' }}
                    >
                        {part}
                    </a>
                );
            }
            return <span key={index}>{part} </span>;
        });

        return <>{parts}</>;
    }, []);

    return (
        <Grid2 container justifyContent={question.is_question ? 'end' : 'start'}>
            <Grid2 maxWidth={200} bgcolor={(t) => alpha(question.is_question ? t.palette.primary.dark : '#a3a3a3', 0.7)} padding={1} borderRadius={3}>
                <Typography color={question.is_question ? 'primary.contrastText' : 'primary.contrastText'} sx={{ wordWrap: 'break-word' }}>
                    {renderMessageWithLinks(question.message[lang] || '')}
                </Typography>
            </Grid2>
        </Grid2>
    )
})

const ChatBotQuestionOptions = ({ questions, shownMessages, setShownMessages, switchPanel }: { questions: IChatbotQuestion[], shownMessages: IChatbotQuestion[], setShownMessages: Dispatch<SetStateAction<IChatbotQuestion[]>>, switchPanel: (panel: TChatPanel) => void }) => {
    const { lang } = useApp()
    const { user } = useAuth()

    const pickedOptionsHistory = useRef<IChatbotQuestion[]>([])

    const traceBackToUncoveredTopic = (question: IChatbotQuestion) => {
        if (question.question_id === 1) {
            console.log('reached root node')
            // We have reached the root node (the first question) and all answers have been shown.
            return question
        }
        const uncoveredTopic = questions.find(q => q.question_id === question.parent_id)
        if (!uncoveredTopic) return questions.find(q => q.question_id === 1) as IChatbotQuestion;
        if (questions.filter(q => q.parent_id === uncoveredTopic.question_id).every(q => pickedOptionsHistory.current.some(m => m.question_id === q.question_id))) {
            return traceBackToUncoveredTopic(uncoveredTopic)
        }
        return uncoveredTopic;
    }

    const questionOptions = useMemo(() => {
        const lastShownMessage = shownMessages[shownMessages.length - 1]
        if (!lastShownMessage) return []
        let options = questions.filter(q => q.parent_id === lastShownMessage.question_id && q.is_question === true)
        if (options.length === 0) {
            // the current sub-topic hierarchy has ended. trace back to an unanswered question in current topic
            const uncoveredTopic = traceBackToUncoveredTopic(lastShownMessage)
            if (uncoveredTopic.question_id === 1) {
                pickedOptionsHistory.current = []
            }
            options = questions.filter(q => q.parent_id === uncoveredTopic.question_id && q.is_question === true)
        }
        console.log(pickedOptionsHistory.current)
        options = options.filter(q => !pickedOptionsHistory.current.find(m => m.question_id === q.question_id))
        return options
    }, [questions, shownMessages, setShownMessages])

    const handlePick = (question: IChatbotQuestion) => {
        const answer = questions.find(q => q.parent_id === question.question_id && q.is_question === false)
        if (!answer) return;
        pickedOptionsHistory.current.push(question)
        pickedOptionsHistory.current.push(answer)
        setShownMessages(v => v.concat(question).concat(answer))
    }

    return (
        <Stack direction={'column'} gap={1} justifyContent={'start'}>
            {questionOptions.map((question) => (
                <Chip key={question.question_id} label={question.message[lang]} color='info' variant='filled' onClick={() => handlePick(question)} />
            ))}
            <Tooltip title={user ? "" : "Login to access live chat"}>
                <span>
                    <Chip style={{ width: '100%' }} disabled={!user} label={'💬 Live Support'} color='secondary' variant='outlined' onClick={() => switchPanel('livechat')} />
                </span>
            </Tooltip>
        </Stack>
    )
}

export default function ChatBotPanel({ switchPanel }: { switchPanel: (panel: TChatPanel) => void }) {
    const [questions, setQuestions] = useState<IChatbotQuestion[]>()
    const [shownMessages, setShownMessages] = useState<IChatbotQuestion[]>([])
    // const [showTopicResetMessage, setShowTopicResetMessage] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchChatBotQuestions();
    }, [])

    useEffect(() => {
        if (!questions || shownMessages.length > 0) return;
        const firstMessage = questions.find(q => q.question_id === 1)
        if (!firstMessage) return console.warn('Unable to find first message for chatbot');
        setShownMessages(v => v.concat(firstMessage))
    }, [questions])

    const scrollToBottom = () => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom(); // Scroll whenever shownMessages changes
        // if (showTopicResetMessage) setShowTopicResetMessage(false)
    }, [shownMessages]);

    // useEffect(() => {
    //     console.log(questions)
    //     console.log(shownMessages)
    // }, [shownMessages, questions])

    const fetchChatBotQuestions = () => {
        api.get('/chatbot/questions').then(res => {
            // exclude questions that dont have answers
            const questions: IChatbotQuestion[] = res.data;
            const filteredQuestions = questions.filter(oq => !oq.is_question ? true : questions.some(iq => !iq.is_question && iq.parent_id === oq.question_id));
            setQuestions(filteredQuestions)
        }).catch(console.error)
    }

    if (!questions || shownMessages.length === 0) return <CircularProgress sx={{ alignSelf: 'center', marginTop: '40%', color: 'secondary.main' }} />

    return (
        <Grid2 container ref={containerRef} padding={2} flexDirection={'column'} overflow={'auto'} wrap='nowrap' gap={1}>
            <Grid2 container justifyContent={'center'} gap={1} >
                <Grid2>
                    <SmartToy color="secondary" />
                </Grid2>
                <Grid2>
                    <Typography color="primary.contrastText">Support Assistant</Typography>
                </Grid2>
            </Grid2>
            <Grid2>
                <Divider orientation='horizontal' color='white' />
            </Grid2>
            <Grid2 container flexDirection={'column'} gap={2}>
                {shownMessages.map((question, index) => (
                    <Grid2 key={index}>
                        <ChatBotMessage question={question} />
                    </Grid2>
                ))}
            </Grid2>
            {/* <Grid2 display={showTopicResetMessage ? 'flex' : 'none'}>
                <Typography>Do you need help with anything else?</Typography>
            </Grid2> */}
            <Grid2>
                <ChatBotQuestionOptions questions={questions} shownMessages={shownMessages} setShownMessages={setShownMessages} switchPanel={switchPanel} />
            </Grid2>
            {/* <Grid2>
                <Typography sx={{ wordWrap: 'break-word' }}>
                    {JSON.stringify(shownMessages)}
                </Typography>
            </Grid2> */}
        </Grid2>
    )
}